/**
 * Playback position — the remaining half of task 0044 step 4.
 *
 * The renderers for video and audio have existed for a while and ingestion
 * landed with decision 0017, so a person can now drop a clip in and watch it.
 * What they could not do was come back to it: every reload started every
 * recording at zero, which for a forty-minute talk is the difference between a
 * usable note and a decoration.
 *
 * The split is the same one `edit.ts` / `edit-host.ts` and `drop.ts` /
 * `drop-host.ts` already use, and for the same reason — there is more than one
 * conformant renderer and the policy must not know which one it is talking to.
 * So `view-state.ts` holds the store and `resumeAt`, the two things worth
 * arguing about, and this file contributes events and a `currentTime`
 * assignment and no policy of its own.
 *
 * **Media events do not bubble.** `loadedmetadata`, `timeupdate`, `pause`,
 * `ended` and `seeked` are all non-bubbling by spec, so the delegated pattern
 * the other two hosts use — one listener on the render root — silently
 * receives nothing. Every listener here is registered with `capture: true`,
 * which is the one way a root sees an event that will never propagate up to
 * it. Written down because a `capture` that looks like a stylistic choice is a
 * `capture` somebody removes.
 *
 * **When the restore happens depends on `preload`,** and the renderer sets
 * `preload="none"` deliberately — decision 0017's argument about handing
 * attacker-controlled bytes to a codec before the user has acted. For a
 * person's own file that costs nothing: Chromium fetches metadata for a
 * same-document `blob:` URL regardless of the hint, so the position is back
 * before they touch the transport. For a recording that lives at a
 * third-party URL there is no metadata until they press play, so the restore
 * lands on that press — playback starts, then jumps to where they were. That
 * is the correct trade rather than an oversight: the alternative is telling
 * that URL's operator who is reading what, for a page somebody merely looked
 * at.
 */

import { readNavigationAnchor } from './edit.js';
import type { IR } from './ir.js';
import { playbackKey, resumeAt, type PlaybackStore } from './view-state.js';

/**
 * How far the playhead must travel before another write.
 *
 * Throttled on MEDIA time rather than a clock: it is the same number at 2×
 * speed, it needs no timer to clean up, and it is checkable without faking a
 * clock in a test. `timeupdate` fires about four times a second, so writing on
 * each one would serialize and re-encode the whole blob roughly 14,000 times
 * an hour to record something nobody reads until the next load.
 *
 * Five seconds is also the resume floor, which is not a coincidence: a
 * position that would not be resumed is not worth a write.
 */
export const RECORD_EVERY_SECONDS = 5;

export interface PlaybackHostOptions {
  /** The element the renderer painted into. */
  readonly root: HTMLElement;
  /** Current IR. Read per event, never cached across a repaint — the role
   *  belonging to a node index is only true for the pass that painted it. */
  readonly current: () => IR | undefined;
  readonly state: PlaybackStore;
}

export interface PlaybackHost {
  detach(): void;
}

type Media = HTMLMediaElement;

/**
 * Structural, not `instanceof`.
 *
 * A media element that arrives from another realm — an iframe, or the jsdom
 * window a test builds — is not an instance of THIS realm's
 * `HTMLMediaElement`, so `instanceof` answers false for an element that is
 * plainly a video. The tag name plus a numeric `currentTime` is the property
 * actually being relied on, and it is the same answer in every realm.
 */
const isMedia = (t: EventTarget | null): t is Media => {
  if (!t || typeof t !== 'object') return false;
  const el = t as Partial<Media>;
  return (el.tagName === 'VIDEO' || el.tagName === 'AUDIO') && typeof el.currentTime === 'number';
};

/**
 * The key for one element, or nothing.
 *
 * Object from the ancestor anchor, role from the IR — never the other way
 * round. A DOM that named its own role could name one it was not painted with,
 * and the same rule already governs every write in `edit-host.ts`.
 *
 * Returns nothing rather than a fallback key when either half is missing. A
 * shared "unknown" bucket would have two recordings overwriting each other's
 * positions, which is worse than not remembering at all.
 */
function keyFor(el: Media, ir: IR | undefined): string | undefined {
  if (!ir) return undefined;
  // Remembering where a person was in a video is not a write — decision 0062.
  // Under `readAnchor` a reader's playback position was never remembered,
  // because the epoch it required is withheld without a write grant.
  const anchor = readNavigationAnchor(el);
  if (!anchor) return undefined;
  const role = ir.nodes[anchor.node]?.role;
  if (!role) return undefined;
  return playbackKey(anchor.object, role);
}

export function attachPlayback(opts: PlaybackHostOptions): PlaybackHost {
  const { root, current, state } = opts;

  /** Elements already restored in this attachment, so a second
   *  `loadedmetadata` — which fires again on any src change, and on some
   *  browsers after a seek past a gap — cannot drag the user back to where
   *  they were when they arrived. */
  const restored = new WeakSet<Media>();
  /** Last written position per element, for the throttle. Per ELEMENT rather
   *  than per key, so two players on one page do not throttle each other. */
  const written = new WeakMap<Media, number>();

  /**
   * Assign, then CHECK — because a seek can be discarded in silence.
   *
   * `loadedmetadata` means the duration is known; it does not mean the
   * resource has a seekable range yet, and a seek to a position outside
   * `seekable` is aborted by the spec without an event or an exception. In
   * Chromium, against a `blob:` WAV whose duration it had just reported as 40
   * seconds, `currentTime = 22` at `loadedmetadata` left the playhead at 0 and
   * raised nothing — the whole feature failing silently, with every unit test
   * green because jsdom's `currentTime` is a field that stores what it is
   * handed. Found by `e2e/playback.mjs`, which is the only place it was
   * visible.
   *
   * So the element is marked restored only once the playhead has actually
   * moved, and the later events get another attempt.
   */
  const seek = (el: Media, at: number): boolean => {
    try { el.currentTime = at; } catch { return false; }
    return Math.abs(el.currentTime - at) < 0.5;
  };

  const restore = (e: Event): void => {
    const el = e.target;
    if (!isMedia(el) || !root.contains(el) || restored.has(el)) return;
    const key = keyFor(el, current());
    if (!key) return;
    const at = resumeAt(state.get(key), el.duration);
    // Nothing to restore is a finished answer, not a reason to keep trying on
    // every subsequent event.
    if (at === undefined) { restored.add(el); return; }
    // Only while the playhead has not moved on its own. A retry arriving after
    // the person started scrubbing would drag them back to where they were
    // last week, which is the failure a retry is worth having only if it
    // cannot cause.
    if (el.currentTime > 0.5) { restored.add(el); return; }
    if (!seek(el, at)) return;
    restored.add(el);
    written.set(el, at);
  };

  /**
   * One writer for every event that can move the playhead, including `ended`.
   *
   * **A position the policy would refuse is stored as no position at all.**
   * That is one rule doing three jobs, and folding them together is what keeps
   * them from disagreeing:
   *
   *  - A recording played to the end holds nothing, rather than an entry
   *    pinned at its duration. `resumeAt` would refuse such an entry anyway,
   *    so this changes no behaviour a person sees — what it buys is a store
   *    that does not grow by one dead entry per recording ever watched, in
   *    somewhere with a quota. View state that only grows is view state that
   *    eventually fails to write the entry somebody needs.
   *  - A person who stops three seconds in leaves nothing behind either, for
   *    the same reason and by the same line.
   *  - And the ordering question disappears. The spec has an element that
   *    reaches its end fire `pause` before `ended`, so a separate `ended`
   *    handler that cleared the entry would be depending on that order holding
   *    in every browser; here both events reach the same writer and reach the
   *    same conclusion.
   */
  const record = (e: Event): void => {
    const el = e.target;
    if (!isMedia(el) || !root.contains(el)) return;
    const t = el.currentTime;
    if (!Number.isFinite(t)) return;
    // Only `timeupdate` is throttled. `pause`, `seeked` and `ended` are
    // deliberate acts and each writes on arrival.
    const last = written.get(el);
    if (e.type === 'timeupdate' && last !== undefined && Math.abs(t - last) < RECORD_EVERY_SECONDS) return;
    const key = keyFor(el, current());
    if (!key) return;
    if (resumeAt(t, el.duration) === undefined) state.clear(key);
    else state.set(key, t);
    written.set(el, t);
  };

  const opt = { capture: true } as const;
  // Three chances at the same restore, and they are not redundant: the
  // duration arrives at `loadedmetadata`, the seekable range usually at
  // `loadeddata`, and `canplay` is the backstop for a resource that buffers
  // slowly. `restore` is idempotent by the `restored` set, so the two that
  // arrive after a successful seek cost a WeakSet lookup each.
  root.addEventListener('loadedmetadata', restore, opt);
  root.addEventListener('loadeddata', restore, opt);
  root.addEventListener('canplay', restore, opt);
  root.addEventListener('timeupdate', record, opt);
  root.addEventListener('pause', record, opt);
  root.addEventListener('seeked', record, opt);
  root.addEventListener('ended', record, opt);

  return {
    detach() {
      root.removeEventListener('loadedmetadata', restore, opt);
      root.removeEventListener('loadeddata', restore, opt);
      root.removeEventListener('canplay', restore, opt);
      root.removeEventListener('timeupdate', record, opt);
      root.removeEventListener('pause', record, opt);
      root.removeEventListener('seeked', record, opt);
      root.removeEventListener('ended', record, opt);
    },
  };
}
